import { httpClient } from '../httpClient';

// Spec 030 US6 — ROPA Reverse Import API helpers (T141).
// Backend routes live on the ROPA blueprint under /ropa/imports.
//
// Wizard flow:
//   Step 1: upload()        → POST /ropa/imports
//   Step 2: getPreview()    → GET  /ropa/imports/:id/preview
//           updateMapping() → PATCH /ropa/imports/:id/mapping
//   Step 3: commit()        → POST /ropa/imports/:id/commit
// Rollback available within 24h via rollback().

export const ropaImportApi = {
  /**
   * Upload a ROPA file (step 1 of wizard).
   * @param {FormData} formData — must include `file` field (.xlsx or .csv, max 10 MB)
   * @returns {ImportedROPASource}
   */
  upload: (formData) =>
    httpClient.post('/ropa/imports', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /**
   * List recent imports for the workspace.
   * @param {{ limit?: number, offset?: number }} params
   */
  list: (params) => httpClient.get('/ropa/imports', { params }),

  /**
   * Get parsed preview with auto-detected column mapping (step 2 of wizard).
   * @param {number} importId
   * @returns {{ source_headers, detected_header_row, auto_mapping, preview_rows }}
   */
  getPreview: (importId) =>
    httpClient.get(`/ropa/imports/${importId}/preview`),

  /**
   * Update column mapping override (step 2 of wizard).
   * @param {number} importId
   * @param {{ mapping: Object.<string,string>, header_row?: number }} body
   * @returns {ImportedROPASource}
   */
  updateMapping: (importId, body) =>
    httpClient.patch(`/ropa/imports/${importId}/mapping`, body),

  /**
   * Commit import — create draft ProcessingPurposes (step 3 of wizard).
   * @param {number} importId
   * @param {{ duplicate_resolutions?: Object.<string,string> }} body
   * @returns {{ import: ImportedROPASource, draft_purpose_ids: number[], cluster_match_suggestions: Array }}
   */
  commit: (importId, body = {}) =>
    httpClient.post(`/ropa/imports/${importId}/commit`, body),

  /**
   * Assign a rejected row (no purpose_name) to an existing active purpose.
   * Creates a draft ROPARecord linked to that purpose.
   * @param {number} importId
   * @param {{ purpose_id: number, row_data?: object }} body
   */
  assignRow: (importId, body) =>
    httpClient.post(`/ropa/imports/${importId}/assign-row`, body),

  /**
   * Roll back an import within 24h.
   * @param {number} importId
   * @returns 200 on success, 410 if rollback window expired
   */
  rollback: (importId) =>
    httpClient.post(`/ropa/imports/${importId}/rollback`),
};
